import React, { useState, useEffect } from 'react';
import { McpBlockedAttempt, ApiResponse } from '../../types';
import { mcpAppsService, SearchParams } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';
import BlockedAttemptsMap from './BlockedAttemptsMap';

// Rejected MCP requests, aggregated per (ip, reason, key prefix) — the MCP
// analog of the Blocked SIP/RTP page. Beyond the counters, each row keeps
// the latest method/tool/User-Agent so a misconfigured client can be
// diagnosed from here without server logs.

const REASON_LABELS: Record<McpBlockedAttempt['reason'], string> = {
  missing_key: 'No API key',
  unknown_key: 'Unknown key',
  disabled_key: 'Disabled key',
  ip_blocked: 'IP not allowed',
  oauth_token: 'Expired or ended sign-in',
};

const McpBlockedTable: React.FC = () => {
  const [view, setView] = useState<'list' | 'map'>('list');
  const [entries, setEntries] = useState<McpBlockedAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    limit: 25,
    search: '',
    sort_by: 'last_attempted_at',
    sort_order: 'DESC',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    limit: 25,
    has_next: false,
    has_prev: false,
  });

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ApiResponse<McpBlockedAttempt> = await mcpAppsService.listBlocked(searchParams);
      setEntries(response.entries || []);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch blocked MCP attempts');
      console.error('Error fetching blocked MCP attempts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const search = new FormData(e.currentTarget).get('search') as string;
    setSearchParams(prev => ({ ...prev, search: search.trim(), page: 1 }));
  };

  const handleSort = (field: string) => {
    setSearchParams(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1,
    }));
  };

  const countryFlag = (cc: string | null): string =>
    cc && /^[A-Za-z]{2}$/.test(cc)
      ? String.fromCodePoint(...Array.from(cc.toUpperCase()).map(c => 0x1f1e6 + c.charCodeAt(0) - 65))
      : '';

  const formatLocation = (entry: McpBlockedAttempt): string => {
    if (!entry.country) return '—';
    const flag = countryFlag(entry.country_code);
    const parts = [entry.country, entry.region, entry.city].filter(Boolean);
    return `${flag ? flag + ' ' : ''}${parts.join(' · ')}`;
  };

  const sortHeader = (field: string, label: string) => (
    <th onClick={() => handleSort(field)} className="sortable">
      {label} {searchParams.sort_by === field && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
    </th>
  );

  if (loading) return <div className="loading">Loading blocked MCP attempts...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="settings-panel">
      <div className="blocked-header-row">
        <h3>Blocked MCP attempts</h3>
        <div className="settings-subnav">
          <button
            type="button"
            className={`nav-button ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`nav-button ${view === 'map' ? 'active' : ''}`}
            onClick={() => setView('map')}
          >
            Map
          </button>
        </div>
      </div>
      <p className="settings-hint">
        Requests rejected by the MCP endpoint (missing/unknown/disabled key, or a valid key used
        from an IP outside its allowlist), aggregated per source IP, reason, and key prefix.
      </p>

      {view === 'map' ? (
        <BlockedAttemptsMap fetchGeo={mcpAppsService.getBlockedGeo} label="blocked MCP request" />
      ) : (
      <>
      <form onSubmit={handleSearch} className="search-form">
        <input name="search" type="text" placeholder="Search by IP, reason, app name, or key prefix..." defaultValue={searchParams.search} />
        <button type="submit">Search</button>
        <button type="button" onClick={() => setSearchParams(prev => ({ ...prev, search: '', page: 1 }))}>Clear</button>
      </form>

      <div className="pagination-info">
        Showing {entries.length} of {pagination.total_count} blocked sources
      </div>

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              {sortHeader('ip', 'IP')}
              <th>Location</th>
              {sortHeader('reason', 'Reason')}
              {sortHeader('app_name', 'App / Key')}
              <th>Last request</th>
              {sortHeader('attempt_count', 'Attempts')}
              {sortHeader('first_attempted_at', 'First seen')}
              {sortHeader('last_attempted_at', 'Last seen')}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={8} className="no-data">No blocked MCP attempts recorded</td></tr>
            ) : (
              entries.map(entry => (
                <tr key={`${entry.ip}-${entry.reason}-${entry.key_prefix}`}>
                  <td className="monospace">{entry.ip}</td>
                  <td className="small" title={entry.org || undefined}>{formatLocation(entry)}</td>
                  <td>
                    <span className={`status ${entry.reason === 'ip_blocked' ? 'status-failed' : 'status-terminated'}`}>
                      {REASON_LABELS[entry.reason] || entry.reason}
                    </span>
                  </td>
                  <td className="small">
                    <div>{entry.app_name || '—'}</div>
                    {entry.key_prefix && <div className="monospace">{entry.key_prefix}…</div>}
                  </td>
                  <td className="small">
                    <div className="monospace">
                      {entry.last_method || '—'}{entry.last_tool ? ` → ${entry.last_tool}` : ''}
                    </div>
                    {entry.last_user_agent && (
                      <div title={entry.last_user_agent}>
                        {entry.last_user_agent.length > 40
                          ? entry.last_user_agent.slice(0, 40) + '…'
                          : entry.last_user_agent}
                      </div>
                    )}
                  </td>
                  <td>{entry.attempt_count.toLocaleString()}</td>
                  <td>{formatDateTime(entry.first_attempted_at)}</td>
                  <td>{formatDateTime(entry.last_attempted_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button disabled={!pagination.has_prev} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page - 1 }))}>Previous</button>
          <span className="page-info">Page {pagination.current_page} of {pagination.total_pages}</span>
          <button disabled={!pagination.has_next} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page + 1 }))}>Next</button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default McpBlockedTable;
